import React, { useEffect, useState, useMemo } from 'react';
import { View, FlatList, RefreshControl, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { showPhotoPickerActionSheet } from '@/utils/common/alert';
import { useNavigation } from '@react-navigation/native';
import { useResidentConversation, useResidentMessaging } from '@/hooks/residents/useMessages';
import { useMarkConversationAsReadMutation } from '@/store/api/admin/messagesApi';
import type { IChatWindowProps, IMessage } from '@/types/admin/messages';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { MessageBubbleMobile } from './MessageBubbleMobile';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useAppSelector } from '@/store';
import { formatFullHeaderDate } from '@/utils/common/formatting';
import MessageInputBar from './MessageInputBar';
import AttachmentPreview from './AttachmentPreview';
import { useChatAttachments } from '@/hooks/residents/useChatAttachments';
import { buildMessageFormData } from '@/services/residents/messages/formData';
import { RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH } from '@/constants/residents/mobile/message';
import { groupMessages, type MessageGroup } from '@/utils/residents/messages/groupMessages';
import { useAutoScrollToBottom } from '@/hooks/residents/useAutoScrollToBottom';
import AttachmentMessageMobile from './AttachmentMessageMobile';
import { useToast } from '@/contexts/admin/ToastContext';
import { logger } from '@/utils/common/logger';

export const ResidentChatWindowMobile: React.FC<IChatWindowProps> = ({ conversationId }) => {
  const { colors } = useThemeColors();
  const { conversation, isLoading, error, refetch } = useResidentConversation(conversationId);
  const { sendMessage, isLoading: isSending } = useResidentMessaging() as any;
  const [markAsRead] = useMarkConversationAsReadMutation();
  const authUserId = useAppSelector((s) => s.auth.user?.id);
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { showError } = useToast();

  const [messageText, setMessageText] = useState('');
  const { attachments, addFromCamera, addFromLibrary, removeAttachment, resetAttachments, handleImageResponse } = useChatAttachments();
  const [refreshing, setRefreshing] = useState(false);
  const { listRef: flatListRef, isNearBottomRef, scrollToBottom: forceScrollToBottom, handleScroll } = useAutoScrollToBottom<FlatList>();

  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerImages, setViewerImages] = useState<string[]>([]);

  const hasMarkedAsReadRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (!conversationId || !conversation) return;

    if (hasMarkedAsReadRef.current !== conversationId) {
      hasMarkedAsReadRef.current = null;
    }

    if (hasMarkedAsReadRef.current === conversationId) return;

    hasMarkedAsReadRef.current = conversationId;
    markAsRead(conversationId).catch(() => {
      hasMarkedAsReadRef.current = null;
    });
  }, [conversationId, conversation, markAsRead]);

  const messageGroups: MessageGroup<IMessage>[] = useMemo(() =>
    groupMessages<IMessage>(conversation?.messages as IMessage[], {
      getHeaderDate: (message) => formatFullHeaderDate(message.createdAt),
      sortDaysBy: 'firstMessageCreatedAt',
    }),
  [conversation?.messages]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (messageGroups.length > 0) {
      timeoutId = setTimeout(() => forceScrollToBottom(true), 150);
    }
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [messageGroups, forceScrollToBottom]);

  const handleSendMessage = async () => {
    if (isSending || (!messageText.trim() && attachments.length === 0)) return;
    const formData = buildMessageFormData({ content: messageText.trim(), isAdmin: false, files: attachments as any });
    try {
      await sendMessage({ conversationId, body: formData }).unwrap();
      setMessageText('');
      resetAttachments();
      try { await refetch(); } catch (e) { logger.warn('chatRefetchFailed', e); }
      setTimeout(() => forceScrollToBottom(false), 300);
    } catch (e) {
      console.error('Failed to send message:', e);
      const msg = (e as any)?.data?.message || (e as any)?.error || 'Failed to send message. Please try again.';
      showError(msg);
    }
  };

  const renderAttachmentMessage = (message: IMessage) => (
    <AttachmentMessageMobile
      key={`files-${message.id}`}
      message={message}
      authUserId={authUserId}
      onOpenImageViewer={(urls, startIndex) => { setViewerImages(urls); setViewerIndex(startIndex); setViewerVisible(true); }}
      onOpenDocument={(url, title) => (navigation as any).navigate('WebView', { url, title })}
    />
  );

  const renderMessageGroup = ({ item }: { item: MessageGroup<IMessage> }) => (
    <View style={styles.messageGroup}>
      <View style={styles.dateHeader}><Text style={[styles.dateText, { color: colors.text.dim }]}>{item.date}</Text></View>
      {item.messages.map((message) => (
        <React.Fragment key={message.id}>
          {!!message.content && (
            <MessageBubbleMobile message={message} authUserId={authUserId} />
          )}
          {!!message.files?.length && renderAttachmentMessage(message)}
        </React.Fragment>
      ))}
    </View>
  );

  const renderMessageInput = () => (
    <>
      {attachments.length > 0 && (
        <AttachmentPreview attachments={attachments as any} onRemove={removeAttachment} />
      )}
      <MessageInputBar
        messageText={messageText}
        setMessageText={setMessageText}
        onSend={handleSendMessage}
        disabled={isSending || (!messageText.trim() && attachments.length === 0)}
        isSending={isSending}
        maxLength={RESIDENTS_MOBILE_MESSAGE_MAX_LENGTH}
        onPressPaperclip={() => {
          showPhotoPickerActionSheet(addFromLibrary, addFromCamera);
        }}
        onFocusScrollToBottom={() => setTimeout(() => forceScrollToBottom(false), 200)}
      />
    </>
  );

  if (isLoading) {
    return <LoadingSpinner message="Loading conversation..." size="large" showMessage={true} />;
  }
  if (error) {
    return (<View style={styles.errorContainer}><Text style={[styles.errorText, { color: colors.text.muted }]}>Failed to load conversation</Text></View>);
  }
  if (!conversation) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={[styles.emptyText, { color: colors.text.subtle }]}>Select a conversation to start messaging</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.messagesContainer}>
        <FlatList
          ref={flatListRef}
          data={messageGroups}
          renderItem={renderMessageGroup}
          keyExtractor={(item) => `${item.date}:${item.messages.length}`}
          style={styles.messagesList}
          contentContainerStyle={styles.messagesContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="none"
          scrollEventThrottle={16}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); try { await refetch(); } finally { setRefreshing(false); } }} tintColor={colors.text.primary} />}
          onScroll={(e) => handleScroll(e.nativeEvent.contentOffset.y, e.nativeEvent.layoutMeasurement.height, e.nativeEvent.contentSize.height, 24)}
          onContentSizeChange={() => isNearBottomRef.current && forceScrollToBottom(true)}
          onLayout={() => setTimeout(() => { if (isNearBottomRef.current) forceScrollToBottom(true); }, 100)}
        />
      </View>
      {/* On Android, FullscreenContent handles bottom safe area; on iOS, add it here */}
      <View style={Platform.OS !== 'android' ? { paddingBottom: insets.bottom } : undefined}>
        {renderMessageInput()}
      </View>

      <ImageViewerModal
        visible={viewerVisible}
        images={viewerImages}
        initialIndex={viewerIndex}
        onClose={() => setViewerVisible(false)}
      />
    </View>
  );
};

export default ResidentChatWindowMobile;

const styles = StyleSheet.create({
  container: { flex: 1 },
  messagesContainer: { flex: 1 },
  messagesList: { flex: 1 },
  messagesContent: { paddingVertical: 8, paddingHorizontal: REDESIGN_SPACING.horizontalPadding },
  dateHeader: { alignItems: 'center', paddingVertical: 20 },
  dateText: { ...REDESIGN_TYPOGRAPHY.caption },
  messageGroup: { marginBottom: 16 },
  errorContainer: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  errorText: { ...REDESIGN_TYPOGRAPHY.body },
  emptyContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  emptyText: { ...REDESIGN_TYPOGRAPHY.body, marginTop: 8 },
});
